/**
 * SCR-018 — My Classes (Lớp của tôi)
 * Route: /teacher
 * Shows only classes where current teacher has a ClassTeacher assignment.
 * Mobile-first: 1 col → 2 col (sm) → 3 col (lg). Filter above card grid.
 */
import { createFileRoute, Link } from '@tanstack/react-router';
import { AlertCircle, BookOpen, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTeacherClasses } from '@client/lib/queries/use-teacher';
import type { TeacherClass } from '@client/lib/queries/use-teacher';
import { authClient } from '@client/lib/auth-client';

export const Route = createFileRoute('/teacher/')({
  component: MyClassesPage,
});

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const STATUS_META: Record<string, { label: string; variant: 'warning' | 'info' | 'success' | 'secondary' | 'destructive' }> = {
  planned:   { label: 'Chờ khai giảng', variant: 'warning' },
  open:      { label: 'Mở ghi danh',    variant: 'info' },
  running:   { label: 'Đang học',        variant: 'success' },
  finished:  { label: 'Đã kết thúc',    variant: 'secondary' },
  cancelled: { label: 'Đã hủy',         variant: 'destructive' },
};

const WEEKDAY_SHORT: Record<string, string> = {
  mon: 'T2', tue: 'T3', wed: 'T4', thu: 'T5', fri: 'T6', sat: 'T7', sun: 'CN',
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
function MyClassesPage() {
  const { data: session } = authClient.useSession();
  const user = session?.user as { name?: string } | undefined;
  const teacherName = user?.name ?? 'Giáo viên';

  const [statusFilter, setStatusFilter] = useState<string>('running');

  const params =
    statusFilter === 'all' ? {} : { status: statusFilter };

  const { data, isLoading, isError, refetch } = useTeacherClasses(params);

  const classes = data ?? [];

  // ---------------------------------------------------------------------------
  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground">Lớp của tôi</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Xin chào, {teacherName}
          </p>
        </div>
        {/* Filter */}
        <div className="w-full sm:w-44">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-full">
              <SelectValue placeholder="Trạng thái" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tất cả trạng thái</SelectItem>
              <SelectItem value="planned">Chờ khai giảng</SelectItem>
              <SelectItem value="open">Mở ghi danh</SelectItem>
              <SelectItem value="running">Đang học</SelectItem>
              <SelectItem value="finished">Đã kết thúc</SelectItem>
              <SelectItem value="cancelled">Đã hủy</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-44 rounded-xl" />
          ))}
        </div>
      )}

      {/* Error state */}
      {isError && !isLoading && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>Không tải được danh sách lớp.</span>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              Thử lại
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Empty state */}
      {!isLoading && !isError && classes.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
          <BookOpen className="h-12 w-12 text-muted-foreground/40" />
          <p className="text-sm font-medium text-muted-foreground">
            Bạn chưa được phân công lớp nào.
          </p>
          <p className="text-xs text-muted-foreground/70">
            Liên hệ quản lý cơ sở để được phân công.
          </p>
        </div>
      )}

      {/* Card grid */}
      {!isLoading && !isError && classes.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {classes.map((cls) => (
            <ClassCard key={cls.id} cls={cls} />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ClassCard
// ---------------------------------------------------------------------------
function ClassCard({ cls }: { cls: TeacherClass }) {
  const statusMeta = STATUS_META[cls.status] ?? { label: cls.status, variant: 'secondary' as const };
  const isCancelled = cls.status === 'cancelled';

  const scheduleText = cls.schedules
    .slice(0, 2)
    .map((s) => {
      const day = s.weekday ? WEEKDAY_SHORT[s.weekday] ?? s.weekday : '';
      return `${day} ${s.startTime}–${s.endTime}`;
    })
    .join(' · ');

  const enrollmentFull = cls.currentEnrollment >= cls.capacity;

  return (
    <Link
      to={'/teacher/classes/$classId/roster' as '/teacher/classes/$classId/roster'}
      params={{ classId: cls.id }}
      className={`block rounded-xl border border-border bg-card transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
        ${isCancelled ? 'opacity-60' : ''}`}
    >
      <Card className="border-0 shadow-none h-full">
        <CardContent className="p-4 flex flex-col gap-3">
          {/* Top row: code + status badge */}
          <div className="flex items-start justify-between gap-2">
            <span className="font-mono text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded shrink-0">
              {cls.code}
            </span>
            <Badge variant={statusMeta.variant} className="text-xs shrink-0">
              {statusMeta.label}
            </Badge>
          </div>

          {/* Class name */}
          <p className="text-sm font-semibold text-foreground line-clamp-2 leading-snug">
            {cls.name}
          </p>

          {/* Meta info */}
          <div className="flex flex-col gap-1 text-xs text-muted-foreground">
            {cls.courseName && (
              <span className="truncate">📚 {cls.courseName}</span>
            )}
            {cls.branchName && (
              <span className="truncate">🏫 {cls.branchName}</span>
            )}
            {scheduleText && (
              <span className="truncate">🕐 {scheduleText}</span>
            )}
          </div>

          {/* Footer: sĩ số + vai trò */}
          <div className="flex items-center justify-between pt-1 border-t border-border mt-auto">
            <span className={`text-xs font-medium ${enrollmentFull ? 'text-destructive' : 'text-muted-foreground'}`}>
              {cls.currentEnrollment}/{cls.capacity} HV
            </span>
            <Badge
              variant={cls.teacherRole === 'lead' ? 'success' : 'outline'}
              className="text-xs"
            >
              {cls.teacherRole === 'lead' ? 'Giáo viên chính' : 'Hỗ trợ'}
            </Badge>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
